/**
 * Navigation & Footer — edit the site chrome end to end.
 *
 * Header: nav links, the Book CTA button, the mobile drawer call button.
 * Footer: tagline, link columns, contact-column note, footer CTA.
 *
 * Both forms are GENERATED from their Zod schemas (same machinery as the
 * section forms). Stored as JSON blobs in the settings table
 * (`nav_config` / `footer_config`) — the site builds its chrome from them
 * D1-first, so edits go live on the next Publish.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  DEFAULT_FOOTER,
  DEFAULT_NAV,
  FOOTER_SETTINGS_KEY,
  FooterConfigSchema,
  NAV_SETTINGS_KEY,
  NavConfigSchema,
} from '@vv/sections';
import { authHeaders } from '~/lib/auth';
import { API_BASE } from '~/lib/config';
import { ZodObjectForm } from '~/lib/formgen';

type SaveState = 'idle' | 'saving' | 'saved' | 'error';
type Rec = Record<string, unknown>;

function parseStored<T>(raw: string | undefined, schema: { safeParse: (v: unknown) => { success: boolean; data?: T } }, fallback: T): T {
  if (raw) {
    try {
      const parsed = schema.safeParse(JSON.parse(raw));
      if (parsed.success && parsed.data) return parsed.data;
    } catch {
      /* fall through to default */
    }
  }
  return JSON.parse(JSON.stringify(fallback)) as T;
}

const CARD: React.CSSProperties = {
  background: 'var(--card)',
  border: '1px solid var(--line-soft)',
  borderRadius: 12,
  padding: 20,
};

const SECTION_HEAD: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: 'var(--text-muted)',
  margin: '0 0 14px',
};

export default function NavigationPage() {
  const [nav, setNav] = useState<Rec | null>(null);
  const [footer, setFooter] = useState<Rec | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  /** Bumped on every edit, so a save only clears "unsaved" if nothing was
   * typed while it was on the wire. */
  const rev = useRef(0);
  const touch = () => {
    rev.current += 1;
    setDirty(true);
    // A new edit re-arms autosave after a failed save.
    setSaveState((s) => (s === 'error' ? 'idle' : s));
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/admin/settings`, { headers: authHeaders(false) });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = (await res.json()) as { settings: Record<string, string> };
        if (cancelled) return;
        setNav(parseStored(data.settings[NAV_SETTINGS_KEY], NavConfigSchema, DEFAULT_NAV) as unknown as Rec);
        setFooter(parseStored(data.settings[FOOTER_SETTINGS_KEY], FooterConfigSchema, DEFAULT_FOOTER) as unknown as Rec);
      } catch (e) {
        if (!cancelled) setLoadError((e as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Validate continuously so the Save button can explain itself.
  const issues = useMemo(() => {
    const out: string[] = [];
    if (nav) {
      const p = NavConfigSchema.safeParse(nav);
      if (!p.success) out.push(...p.error.issues.slice(0, 4).map((i) => `header · ${i.path.join('.')}: ${i.message}`));
    }
    if (footer) {
      const p = FooterConfigSchema.safeParse(footer);
      if (!p.success) out.push(...p.error.issues.slice(0, 4).map((i) => `footer · ${i.path.join('.')}: ${i.message}`));
    }
    return out;
  }, [nav, footer]);

  async function save() {
    if (!nav || !footer || issues.length > 0) return;
    const savedRev = rev.current;
    setSaveState('saving');
    setSaveError(null);
    try {
      // VV settings contract: PATCH / with { values: { key: value } }.
      const res = await fetch(`${API_BASE}/api/admin/settings`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({
          values: {
            [NAV_SETTINGS_KEY]: JSON.stringify(nav),
            [FOOTER_SETTINGS_KEY]: JSON.stringify(footer),
          },
        }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      setSaveState('saved');
      if (rev.current === savedRev) setDirty(false);
      window.setTimeout(() => setSaveState((s) => (s === 'saved' ? 'idle' : s)), 2500);
    } catch (e) {
      setSaveState('error');
      setSaveError((e as Error).message);
    }
  }

  // Autosave, like every other screen in the editor: once the forms are
  // valid and something changed, save after a short pause. (It used to wait
  // for a "Save chrome" click, so navigating away silently lost edits.)
  useEffect(() => {
    // After a failed save, wait for the next edit (or a manual Save click)
    // instead of hammering the API.
    if (!dirty || issues.length > 0 || saveState === 'saving' || saveState === 'error') return;
    const t = window.setTimeout(() => void save(), 900);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nav, footer, dirty, issues.length, saveState]);

  // Closing the tab mid-edit (or mid-save) gets a warning.
  useEffect(() => {
    if (!dirty && saveState !== 'saving') return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty, saveState]);

  if (loadError) {
    return <div className="content-editor__error">Couldn't load settings: {loadError}</div>;
  }
  if (!nav || !footer) {
    return <p style={{ color: 'var(--text-muted)' }}>Loading site chrome…</p>;
  }

  return (
    <div style={{ maxWidth: 760, display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
        <h1
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 24,
            fontWeight: 800,
            color: 'var(--primary)',
            margin: 0,
          }}
        >
          Navigation &amp; Footer
        </h1>
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
          Site-wide — the header and footer on every page. Saved changes go live on the next Publish.
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, alignItems: 'center' }}>
          {saveState === 'saved' && (
            <span style={{ fontSize: 12, color: 'var(--teal)' }}>✓ Saved — goes live on Publish</span>
          )}
          {saveState === 'error' && (
            <span style={{ fontSize: 12, color: '#c44545' }} title={saveError ?? ''}>
              ⚠ {saveError ?? 'Save failed'}
            </span>
          )}
          {dirty && saveState !== 'saving' && issues.length === 0 && (
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Unsaved changes</span>
          )}
          <button
            type="button"
            className="content-editor__save"
            disabled={saveState === 'saving' || issues.length > 0 || !dirty}
            onClick={() => void save()}
            style={{ cursor: saveState === 'saving' ? 'wait' : 'pointer' }}
          >
            {saveState === 'saving' ? 'Saving…' : 'Save chrome'}
          </button>
        </div>
      </div>

      {issues.length > 0 && (
        <div
          style={{
            padding: '10px 14px',
            background: 'rgba(220,80,80,0.08)',
            border: '1px solid rgba(220,80,80,0.3)',
            borderRadius: 8,
            fontSize: 12.5,
            color: '#c44545',
          }}
        >
          <strong>Fix before saving:</strong>
          <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
            {issues.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ul>
        </div>
      )}

      <div style={CARD}>
        <p style={SECTION_HEAD}>Header — links + booking button</p>
        <ZodObjectForm
          schema={NavConfigSchema}
          value={nav}
          onChange={(next) => {
            setNav(next);
            touch();
          }}
        />
      </div>

      <div style={CARD}>
        <p style={SECTION_HEAD}>Footer — tagline, columns, CTA</p>
        <ZodObjectForm
          schema={FooterConfigSchema}
          value={footer}
          onChange={(next) => {
            setFooter(next);
            touch();
          }}
        />
      </div>

      <p style={{ fontSize: 11.5, color: 'var(--text-muted)', margin: 0 }}>
        The footer's phone, email, and town list follow Settings → Business automatically —{' '}
        <Link to="/settings" style={{ color: 'var(--teal)', fontWeight: 600 }}>
          edit them in Settings
        </Link>
        , not here. Keep the booking button pointed at /book: that page offers both the free
        consult and the direct-signup link.
      </p>
    </div>
  );
}
